// Colours as TradingView's pickers handle them: a colour plus an opacity, stored
// as "#RRGGBB" or "#RRGGBBAA" (upper case). Pure functions, no DOM.

export interface Rgba {
  readonly r: number
  readonly g: number
  readonly b: number
  /** 0 to 1. */
  readonly a: number
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const fromHex = (hex: string): Rgba | null => {
  const digits = hex.length === 3 || hex.length === 4 ? [...hex].map((d) => d + d).join("") : hex
  if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(digits)) return null
  const at = (i: number) => parseInt(digits.slice(i, i + 2), 16)
  return { r: at(0), g: at(2), b: at(4), a: digits.length === 8 ? at(6) / 255 : 1 }
}

/** #rgb, #rgba, #rrggbb, #rrggbbaa, rgb(), rgba() (comma or space syntax), "transparent". */
export const parseColor = (css: string): Rgba | null => {
  const text = css.trim().toLowerCase()
  if (text === "transparent") return { r: 0, g: 0, b: 0, a: 0 }
  if (text.startsWith("#")) return fromHex(text.slice(1))
  const fn = /^rgba?\(([^)]*)\)$/.exec(text)
  if (fn === null) return null
  const parts = fn[1]!.split(/[\s,/]+/).filter(Boolean)
  if (parts.length < 3 || parts.length > 4) return null
  const channel = (part: string) => (part.endsWith("%") ? (parseFloat(part) / 100) * 255 : parseFloat(part))
  const [r, g, b] = parts.slice(0, 3).map(channel) as [number, number, number]
  const alphaPart = parts[3]
  const a = alphaPart === undefined ? 1 : alphaPart.endsWith("%") ? parseFloat(alphaPart) / 100 : parseFloat(alphaPart)
  if (![r, g, b, a].every(Number.isFinite)) return null
  return { r: clamp(r, 0, 255), g: clamp(g, 0, 255), b: clamp(b, 0, 255), a: clamp(a, 0, 1) }
}

const hex2 = (value: number) => Math.round(clamp(value, 0, 255)).toString(16).padStart(2, "0").toUpperCase()

/** "#RRGGBB" when opaque, else "#RRGGBBAA". */
export const toHex = ({ a, b, g, r }: Rgba): string =>
  `#${hex2(r)}${hex2(g)}${hex2(b)}${a >= 1 ? "" : hex2(a * 255)}`

/** Any CSS colour this module reads, as the stored form; null when it isn't one. */
export const normalizeColor = (css: string): string | null => {
  const rgba = parseColor(css)
  return rgba === null ? null : toHex(rgba)
}

/** The same colour at another opacity (0 to 1). */
export const withOpacity = (css: string, opacity: number): string => {
  const rgba = parseColor(css) ?? { r: 0, g: 0, b: 0, a: 1 }
  return toHex({ ...rgba, a: clamp(opacity, 0, 1) })
}

export const opacityOf = (css: string): number => parseColor(css)?.a ?? 1

/** The colour without its opacity: "#RRGGBB". */
export const opaque = (css: string): string => withOpacity(css, 1)

/** Same colour once both are parsed (rgba(…) vs #RRGGBBAA rounding is tolerated). */
export const sameColor = (a: string | undefined, b: string | undefined, { ignoreAlpha = false } = {}): boolean => {
  if (a === undefined || b === undefined) return a === b
  const x = parseColor(a)
  const y = parseColor(b)
  if (x === null || y === null) return a === b
  const rgb = Math.abs(x.r - y.r) < 0.6 && Math.abs(x.g - y.g) < 0.6 && Math.abs(x.b - y.b) < 0.6
  return rgb && (ignoreAlpha || Math.abs(x.a - y.a) < 0.01)
}

/** Fully transparent: drawn as nothing. */
export const isTransparent = (css: string | undefined): boolean => css !== undefined && parseColor(css)?.a === 0

/**
 * TradingView's colour grid: greys, the ten main hues, then tints and shades of
 * each hue (one column per hue).
 */
export const TV_PALETTE: ReadonlyArray<ReadonlyArray<string>> = [
  ["#FFFFFF", "#D1D4DC", "#B2B5BE", "#9598A1", "#787B86", "#5D606B", "#434651", "#2A2E39", "#131722", "#000000"],
  ["#F23645", "#FF9800", "#FFEB3B", "#4CAF50", "#089981", "#00BCD4", "#2962FF", "#673AB7", "#9C27B0", "#E91E63"],
  ["#FCCBCD", "#FFE0B2", "#FFF9C4", "#C8E6C9", "#ACE5DC", "#B2EBF2", "#BBD9FB", "#D1C4E9", "#E1BEE7", "#F8BBD0"],
  ["#FAA1A4", "#FFCC80", "#FFF59D", "#A5D6A7", "#70CCBD", "#80DEEA", "#90BFF9", "#B39DDB", "#CE93D8", "#F48FB1"],
  ["#F77C80", "#FFB74D", "#FFF176", "#81C784", "#42BDA8", "#4DD0E1", "#5B9CF6", "#9575CD", "#BA68C8", "#F06292"],
  ["#F7525F", "#FFA726", "#FFEE58", "#66BB6A", "#22AB94", "#26C6DA", "#3179F5", "#7E57C2", "#AB47BC", "#EC407A"],
  ["#B22833", "#F57C00", "#FBC02D", "#388E3C", "#056656", "#0097A7", "#1848CC", "#512DA8", "#7B1FA2", "#C2185B"],
  ["#801922", "#E65100", "#F57F17", "#1B5E20", "#00332A", "#006064", "#0C3299", "#311B92", "#4A148C", "#880E4F"]
]
